import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import PublicPageShell from '../components/landing/PublicPageShell';
import { FileText, Scale, AlertTriangle, Ban, RefreshCw, Globe, Mail } from 'lucide-react';

// Même titre de rubrique que le centre d'aide : filet fin, icône monochrome.
const Section = ({ icon: Icon, title, children }) => (
  <section className="landing-help-section">
    <h2>
      <Icon size={17} strokeWidth={1.75} aria-hidden="true" />
      {title}
    </h2>
    <div className="landing-help-body">{children}</div>
  </section>
);

const TermsOfService = () => {
  const { language, t } = useLanguage();
  const isFr = language === 'fr';

  return (
    <PublicPageShell
      eyebrow={t('footerLegal')}
      title={t('termsOfService')}
      lede={t('lastUpdated')}
    >
      <SEOHead
        title={t('seoTermsTitle')}
        description={t('seoTermsDesc')}
        canonical="/terms"
      />
      <div className="landing-prose" style={{ maxWidth: '820px' }}>

        <Section icon={FileText} title={t('tosPurpose')}>
          <p>{isFr
            ? 'Les présentes conditions générales d\'utilisation (CGU) régissent l\'accès et l\'utilisation de la plateforme FretManager, accessible via l\'application web. En créant un compte, vous acceptez sans réserve ces conditions.'
            : 'These terms of service govern access to and use of the FretManager platform, accessible via the web application. By creating an account, you accept these terms without reservation.'}
          </p>
        </Section>

        <Section icon={Scale} title={t('tosServiceDescription')}>
          <p>{isFr
            ? 'FretManager est une plateforme SaaS de gestion de fret international permettant :'
            : 'FretManager is a SaaS platform for international freight management enabling:'}
          </p>
          <ul className="list-none space-y-2 mt-2">
            {(isFr ? [
              'La création et le suivi de colis avec code OCE unique',
              'La gestion des départs (carréaisons) aériens et maritimes',
              'La collaboration multi-acteurs (admin, collecteurs, livreurs, clients)',
              'La facturation automatique et le suivi financier',
              'Les notifications multi-canal (email, WhatsApp, in-app)',
            ] : [
              'Creating and tracking packages with unique OCE codes',
              'Managing air and maritime shipments',
              'Multi-actor collaboration (admin, collectors, delivery partners, clients)',
              'Automatic invoicing and financial tracking',
              'Multi-channel notifications (email, WhatsApp, in-app)',
            ]).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: 'hsl(var(--accent))' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Globe} title={t('tosRegistration')}>
          <p>{isFr
            ? 'L\'utilisateur s\'engage à fournir des informations exactes lors de l\'inscription. Chaque compte est personnel et non-transférable. L\'utilisateur est responsable de la confidentialité de ses identifiants.'
            : 'Users must provide accurate information during registration. Each account is personal and non-transferable. Users are responsible for keeping their credentials confidential.'}
          </p>
        </Section>

        <Section icon={Ban} title={t('tosProhibitedUses')}>
          <ul className="list-none space-y-2">
            {(isFr ? [
              'Utiliser le service à des fins illégales ou frauduleuses',
              'Tenter d\'accéder aux données d\'autres entreprises',
              'Surcharger volontairement les serveurs ou exploiter des failles',
              'Revendre ou redistribuer le service sans autorisation',
              'Transporter des marchandises interdites ou réglementées sans déclaration',
            ] : [
              'Using the service for illegal or fraudulent purposes',
              'Attempting to access other companies\' data',
              'Deliberately overloading servers or exploiting vulnerabilities',
              'Reselling or redistributing the service without authorization',
              'Shipping prohibited or regulated goods without declaration',
            ]).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: 'hsl(var(--accent))' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={AlertTriangle} title={t('tosLiability')}>
          <p>{isFr
            ? 'FretManager fournit un outil de gestion et de suivi. Nous ne sommes pas transporteur et ne saurions être tenus responsables des pertes, retards ou dommages liés au transport physique des marchandises. Notre responsabilité est limitée au bon fonctionnement de la plateforme.'
            : 'FretManager provides a management and tracking tool. We are not a carrier and cannot be held liable for losses, delays, or damages related to the physical transport of goods. Our liability is limited to the proper functioning of the platform.'}
          </p>
        </Section>

        <Section icon={RefreshCw} title={t('tosSubscriptions')}>
          <p>{isFr
            ? 'L\'accès à FretManager est soumis à un abonnement mensuel ou annuel. Chaque nouveau compte bénéficie d\'une période d\'essai de 14 jours. La résiliation peut être effectuée à tout moment depuis les paramètres du compte. Les données sont conservées 30 jours après résiliation.'
            : 'Access to FretManager requires a monthly or annual subscription. Each new account benefits from a 14-day trial period. Cancellation can be done at any time from account settings. Data is retained for 30 days after cancellation.'}
          </p>
        </Section>

        <Section icon={Mail} title={t('tosContact')}>
          <div className="mt-3 rounded-xl border p-4" style={{ borderColor: 'hsl(var(--accent) / 0.3)', background: 'hsl(var(--accent) / 0.05)' }}>
            <p className="font-semibold" style={{ color: 'hsl(var(--foreground))' }}>FretManager — {t('legalDepartment')}</p>
            <p className="mt-1">Email : legal@fretmanager.com</p>
          </div>
        </Section>

      </div>
    </PublicPageShell>
  );
};

export default TermsOfService;
